import { useLayoutEffect } from "react";

/**
 * Shrinks text until it fits its box, so admin content at the maximum lengths
 * (150-char title, 6 x 200-char rules, 350-char questions) stays fully visible.
 *
 * Sets the CSS variable --fit (1 = full size, down to `min`) on the element; size the
 * text inside it with calc(var(--fit, 1) * <size>). If the text still overflows at
 * `min`, the element gets the class "is-overflowing" so CSS can let it scroll.
 */
export default function useFitText(ref, deps, { min = 0.55, step = 0.05 } = {}) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const overflows = () =>
      el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;

    const fit = () => {
      let scale = 1;
      el.classList.remove("is-overflowing");
      el.style.setProperty("--fit", String(scale));
      while (scale > min && overflows()) {
        scale = Math.max(min, Number((scale - step).toFixed(2)));
        el.style.setProperty("--fit", String(scale));
      }
      if (overflows()) el.classList.add("is-overflowing");
    };

    // Size after our own last fit; changes we caused ourselves must not trigger a re-fit.
    let fittedSize = "";
    const sizeKey = () => `${el.clientWidth}x${el.clientHeight}`;
    const runFit = () => {
      fit();
      fittedSize = sizeKey();
    };

    runFit();

    // Re-fit when the box is resized from outside (window resize, rotation). Deferred to
    // the next frame so we never change layout inside the ResizeObserver callback, which
    // the browser reports as "ResizeObserver loop completed with undelivered notifications".
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (sizeKey() !== fittedSize) runFit();
      });
    });
    observer.observe(el);

    let active = true;
    document.fonts?.ready?.then(() => active && runFit());
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
